import { useGetDoctorsNotes } from '@viome/viome-base/src/usecase/Health/HealthQuery';
import {
  ScoreLevel,
  IDoctorsNoteResponse,
} from '@viome/viome-base/src/usecase/Health/Health.model';
import { StandardContainer } from '@design-system/StandardContainer';
import { LongevitySection } from '@screens/HealthPages/LongevitySection';
import { HealthZonesSection } from '@screens/HealthPages/HealthZonesSection';
import { MicrobiomeProfileSection } from '@screens/HealthPages/MicrobiomeProfileSection';
import { HealthConnectionsSection } from '@screens/HealthPages/HealthConnectionsSection';
import { NutritionPlanSection } from '@screens/HealthPages/NutritionPlanSection';
import { NutritionJourneyDeeperSection } from '@screens/NutritionPage/NutritionJourneyDeeperSection';
import { ScoreListSection } from '@screens/HealthPages/ScoreListSection';
import { JourneyEducationTypes } from '@utils/JourneyEducationTypes';
import { HEALTH_INSIGHT_CARD_BACKGROUND } from '@/constants/images';
import { useKitType } from '@/hooks/useKittype';
import EducationVideoCarousel from '../HomePage/EducationVideoCarousel/EducationVideoCarousel';
import { Render } from '@/components/Helpers/ComponentRender/Render';
import { useState } from 'react';
import { DoctorsNotes } from '@screens/LevelTwoDetail/DoctorsNotes';

const GI_SCORE_ID = 1369;
const OHI_SCORE_ID = 1400;

export const HealthContainer = () => {
  const kitType = useKitType();

  const { data: doctorsData } = useGetDoctorsNotes(
    ScoreLevel.L2,
    kitType === 'OHI' ? OHI_SCORE_ID : GI_SCORE_ID,
  );

  const doctorNotes: IDoctorsNoteResponse = doctorsData || [];
  const [healthConnectionLoading, setHealthConnectionLoading] = useState(false);
  const [bgIsLoading, setBgIsLoading] = useState(true);

  const renderDoctorsNoteWithBg = () => {
    if (!doctorNotes) {
      return;
    }
    return (
      <section
        className="bg-black bg-cover bg-center"
        style={{
          backgroundImage: `linear-gradient(to bottom, rgba(0, 0, 0, 1) 0%, rgba(0, 0, 0, 0) 30%, rgba(0, 0, 0, 1) 100%), url('https://viome-cdn.s3.amazonaws.com/media/app/v2/png/home/image_for_doctors_notes.png')`,
        }}
      >
        <StandardContainer>
          <DoctorsNotes notes={doctorNotes} />
        </StandardContainer>
      </section>
    );
  };

  const renderFBIContent = () => (
    <StandardContainer>
      <HealthZonesSection />

      <LongevitySection />

      <MicrobiomeProfileSection />

      <HealthConnectionsSection
        isQueryLoading={(data) => setHealthConnectionLoading(data)}
      />

      <NutritionPlanSection isLoading={healthConnectionLoading} />
    </StandardContainer>
  );

  const renderGIContent = () => (
    <>
      <StandardContainer>
        <ScoreListSection
          title="My Gut & Digestive Health"
          description="Your scores provide a clear picture of how your gut health influences and supports key areas of your overall well-being."
        />
      </StandardContainer>
      {renderDoctorsNoteWithBg()}
      <StandardContainer>
        <MicrobiomeProfileSection />

        <HealthConnectionsSection
          isQueryLoading={(data) => setHealthConnectionLoading(data)}
        />

        <NutritionPlanSection isLoading={healthConnectionLoading} />
      </StandardContainer>
    </>
  );

  const renderOHIContent = () => (
    <>
      <StandardContainer>
        <ScoreListSection
          title="My Oral Health"
          description="Your scores provide a clear picture of how your oral microbiome influences and supports your gums, teeth, and overall oral cavity."
        />
      </StandardContainer>
      {renderDoctorsNoteWithBg()}
      <StandardContainer>
        <MicrobiomeProfileSection />
        <NutritionPlanSection isLoading={healthConnectionLoading} isOHI />
      </StandardContainer>
    </>
  );

  const renderHIContent = () => (
    <StandardContainer>
      <LongevitySection />

      <HealthZonesSection />

      <MicrobiomeProfileSection />

      <HealthConnectionsSection
        isQueryLoading={(data) => setHealthConnectionLoading(data)}
      />

      <NutritionPlanSection isLoading={healthConnectionLoading} />
    </StandardContainer>
  );

  const contentMap = {
    FBI: renderFBIContent,
    GI: renderGIContent,
    OHI: renderOHIContent,
    HI: renderHIContent,
  };

  const ContentComponent = contentMap[kitType as keyof typeof contentMap];

  return (
    <div className="relative">
      <StandardContainer>
        <div className="py-0">
          <div className="flex flex-row items-center pt-8">
            <h1 className="dark text-primary typography-d2 relative">Health</h1>
          </div>
        </div>
      </StandardContainer>

      <div className="relative w-full flex justify-center items-center">
        {bgIsLoading && (
          <div
            className="w-full max-h-[26rem] bg-gray-900 animate-pulse mb-12"
            style={{ height: '26rem' }}
          />
        )}
        <img
          aria-hidden
          src={HEALTH_INSIGHT_CARD_BACKGROUND}
          className={`w-full max-h-[26rem] object-cover ${bgIsLoading ? 'hidden' : ''}`}
          onLoad={() => setBgIsLoading(false)}
        />
      </div>

      <Render when={kitType}>
        <ContentComponent />
        <StandardContainer>
          <div className="mt-6">
            <EducationVideoCarousel screen="health" />
          </div>
          <div className="pb-24">
            <NutritionJourneyDeeperSection
              sectionInfo={JourneyEducationTypes.HEALTH_HOME}
              screen="health"
            />
          </div>
        </StandardContainer>
      </Render>
    </div>
  );
};
